import * as React from 'react';

import { SortableHandle } from 'react-sortable-hoc';

import { AnimatePresence, motion } from 'framer-motion';

import { Translate } from 'react-localize-redux';

import styled, { css } from 'styled-components';

import Chip from '@components/basics/chip';
import Flex from '@components/layout/flex';
import { opacityMotion } from '@components/framer';
import PlusIcon from '@components/basics/icons/plus';
import Typography from '@components/basics/typography';
import HandleIcon from '@components/basics/icons/handle';
import CleanIcon from '@components/basics/icons/track/clean-icon';
import ProgressbarInline from '@components/basics/progressbar-inline';
import ExplicitIcon from '@components/basics/icons/track/explicit-icon';
import InstrumentalIcon from '@components/basics/icons/track/instrumental-icon';
import InstantGratificationIcon from '@components/basics/icons/track/instant-gratification-icon';

import LoadingCtrl from '@webapp/controllers/loading';
import Pill from '@components/basics/pill';

export interface SidebarItemSongProps {
  className?: string;
  number: string;
  text: string;
  filename?: string;
  done?: boolean;
  active?: boolean;
  uploading?: boolean;
  progress?: number;
  error?: boolean;
  isClean?: boolean;
  isExplicit?: boolean;
  isInstrumental?: boolean;
  isInstantGratification?: boolean;
  isNFT?: boolean;
  isNFTExlusive?: boolean;
  isLoading: boolean;
  onRetry: () => void;
  onRemove: () => void;

  hideRemoveOption?: boolean;
  disableRemove?: boolean;
  disableSorting?: boolean;
}

const Handle = SortableHandle((props: { className?: string }) => (
  <HandleIcon isDark {...props} />
));

export const SidebarItemSong: React.FC<SidebarItemSongProps> = (props) => {
  const [isHover, setIsHover] = React.useState(false);
  return (
    <motion.div
      className={props.className}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <div className="wrapper-sidebar-item">
        <NameWrapper justifyContent="flex-start" alignItems="center">
          {!props.error ? (
            <Typography
              className="number"
              type={
                props.done
                  ? 'circle-success'
                  : props.active
                  ? 'circle-fill'
                  : 'circle-grey'
              }
            >
              {props.number}
            </Typography>
          ) : (
            <Typography className="number" type="circle-error">
              {props.number}
            </Typography>
          )}
          {!props.disableSorting ? <Handle className="icon-handle" /> : null}
          <div className="name">
            <Typography className="text" weight="700">
              {props.text.length > 20
                ? `${props.text.slice(0, 20)}...`
                : props.text}
            </Typography>
          </div>
          {props.isNFT && <Pill className="nft-pill">NFT</Pill>}
        </NameWrapper>
        <div className="action" onClick={(ev) => ev.stopPropagation()}>
          {props.uploading ? (
            <ProgressbarInline progress={props.progress} />
          ) : props.isLoading ? (
            <LoadingCtrl
              className="loader"
              loading={props.isLoading}
              size="64px"
            />
          ) : null}
          <AnimatePresence exitBeforeEnter>
            {!props.uploading &&
            !props.hideRemoveOption &&
            (props.error || !props.disableRemove) ? (
              <CircleIcon
                initial="rest"
                animate={isHover ? 'hover' : 'rest'}
                variants={opacityMotion}
                onClick={(ev) => {
                  ev.stopPropagation();
                  props.onRemove();
                }}
              >
                <PlusIcon close isWhite />
              </CircleIcon>
            ) : null}
          </AnimatePresence>
        </div>
      </div>
      {!props.error && !props.uploading
        ? props.isClean && (
            <IconWrapper>
              <CleanIcon isInvertedTheme />
            </IconWrapper>
          )
        : null}
      {!props.error && !props.uploading
        ? props.isExplicit && (
            <IconWrapper>
              <ExplicitIcon isInvertedTheme />
            </IconWrapper>
          )
        : null}
      {!props.error && !props.uploading
        ? props.isInstrumental && (
            <IconWrapper>
              <InstrumentalIcon isInvertedTheme />
            </IconWrapper>
          )
        : null}
      {!props.error && !props.uploading
        ? props.isInstantGratification && (
            <IconWrapper>
              <InstantGratificationIcon isInvertedTheme />
            </IconWrapper>
          )
        : null}
      {!props.error && !props.uploading
        ? props.isNFTExlusive && (
            <IconWrapper>
              <Chip smaller smallFont chipType="blue">
                <Translate id="RELEASE.SIDEBAR.LIST.NFT_EXCLUSIVE" />
              </Chip>
            </IconWrapper>
          )
        : null}
    </motion.div>
  );
};

export default styled(SidebarItemSong)`
  .wrapper-sidebar-item {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    user-select: none;
    position: relative;

    ${(props) =>
      !props.disableSorting
        ? css`
            &:hover {
              .number {
                opacity: 0;
              }

              .icon-handle {
                opacity: 1;
              }
            }
          `
        : ''}

    .number {
      margin-right: 10px;
      flex-shrink: 0;
      height: 24px;
      width: 24px;
      line-height: 24px;
      border-radius: 50%;
      transition: opacity
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`},
        background-color
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`};
    }

    .nft-pill {
      margin-left: 10px;
    }

    .icon-handle {
      cursor: grab;
      position: absolute;
      top: 50%;
      left: 0;
      transform: translate(0, -50%);
      z-index: 3;
      opacity: 0;
      transition: opacity
        ${({ theme }) =>
          `${theme.transitions.duration} ${theme.transitions.type}`};
    }

    .loader {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translate(0, -50%);
    }

    .action {
      flex-shrink: 0;
      flex-grow: 0;
      position: relative;
      display: flex;
      justify-content: flex-end;
      align-items: center;

      .song-type {
        opacity: 1;
        position: absolute;
        top: 50%;
        right: 0;
        transform: translate(0, -50%);

        transition: opacity
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`};
      }
    }

    ${(props) =>
      !props.active
        ? css`
            .text {
              opacity: 0.5;
            }
          `
        : null}

    ${(props) =>
      !props.error
        ? css`
          &:hover {
            .action {
              .song-type {
                opacity: 0;
                pointer-events: none;
              
            }
            .loader {
              opacity: 0;
              pointer-events: none;
            }
          }
        `
        : null}
  }
`;

const IconWrapper = styled.div`
  margin-top: 10px;
  margin-left: 34px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 5px;
`;

const NameWrapper = styled(Flex)`
  flex-grow: 1;
`;

const CircleIcon = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  background-color: ${({ theme }) => theme.colors.accent};
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover {
    background-color: ${({ theme }) => theme.colors.accentHover};

    .icon {
      path {
        fill: ${({ theme }) => theme.colors.white};
      }
    }
  }
`;
